/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  getDocs,
  getCountFromServer
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { importClassrooms } from "../../../services/firebaseService";
import { Class } from "./classe.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { User } from "../user/user.type";

const enrichClassroomTeacher = async (classroom: any) => {
  const assignedTeacher = classroom.teacherId || classroom.profId || classroom.teacher || classroom.professeur;
  const teacherId = typeof assignedTeacher === "string"
    ? assignedTeacher
    : assignedTeacher?.uid || assignedTeacher?.id;
  if (!teacherId) return classroom;

  try {
    const teacherSnapshot = await getDoc(doc(db, "users", teacherId));
    return {
      ...classroom,
      teacherData: teacherSnapshot.exists()
        ? { uid: teacherSnapshot.id, ...teacherSnapshot.data() }
        : undefined,
    };
  } catch {
    return classroom;
  }
};


export const ClassApi = createApi({
  reducerPath: "classApi",
  tagTypes: ["class", "classprofs", "classetablissements"],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    addOrEditClass: builder.mutation({
      async queryFn({ id, ...data }) {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }
          const userId = user?.uid;
          if (!id) {
            const docRef = await addDoc(collection(db, "classrooms"), { ...data, createdAt: serverTimestamp(), createdBy: userId, updatedAt: serverTimestamp() });
            return {
              data: {
                id: docRef.id,
                message: "Classe créée !",
                ...data,
              },
            };
          } else {
            // Mettre à jour Firestore
            const userRef = doc(db, "classrooms", id);
            await updateDoc(userRef, { ...data, updatedAt: serverTimestamp() });

            return {
              data: {
                message:
                  "Classe modifiée !",
              },
            };
          }
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["class", "classprofs", "classetablissements"],
    }),
    getClass: builder.query({
      async queryFn() {
        try {
          return new Promise((resolve) => {
            const q = query(collection(db, "classrooms"), orderBy("createdAt", "desc"),);
            onSnapshot(q, (snapshot) => {
              const classrooms = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              resolve({ data: classrooms as Class[] });
            });
            // unsubscribe();
          });
        } catch (error) {
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const classRef = query(collection(db, "classrooms"), orderBy("createdAt", "desc"),);
        let unsubscribe;
        try {
          await cacheDataLoaded;

          unsubscribe = onSnapshot(classRef, (snapshot) => {
            const classes = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            updateCachedData(() => classes as Class[]);
          });
        } catch (error) {
          console.error("Erreur de souscription:", error);
        }

        // Nettoyer l'écouteur Firestore quand le cache est supprimé
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: ["class"],
    }),

    getClassroomsByProf: builder.query({
      async queryFn() {
        try {
          const user = auth.currentUser;
          if (!user) return { error: { message: "Aucun utilisateur connecté." } };

          const q = query(
            collection(db, "classrooms"),
            where("createdBy", "==", user.uid),
            orderBy("createdAt", "desc")
          );

          const snapshot = await getDocs(q);
          const classrooms = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as any[];

          return { data: classrooms };
        } catch (error) {
          console.error("ERREUR queryFn:", error);
          return { error };
        }
      },

      keepUnusedDataFor: 60,

      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;

          const user = auth.currentUser;
          if (!user) return;

          const q = query(
            collection(db, "classrooms"),
            where("createdBy", "==", user.uid),
            orderBy("createdAt", "desc")
          );

          unsubscribe = onSnapshot(q, async (snapshot) => {
            const classrooms = snapshot.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            })) as any[];

            updateCachedData(() => classrooms);
          });

        } catch {
          // silencieux
        }

        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },

      providesTags: ["classprofs"],
    }),

    getClassroomsByEtablissement: builder.query({
      async queryFn(etablissementId?: string) {
        try {
          const userId = etablissementId || auth.currentUser?.uid;
          if (!userId) {
            return { error: { message: "Aucun établissement connecté." } };
          }

          const classroomsQuery = query(
            collection(db, "classrooms"),
            where("createdBy", "==", userId),
            orderBy("createdAt", "desc"),
          );
          const snapshot = await getDocs(classroomsQuery);
          const classrooms = await Promise.all(snapshot.docs.map((classroomDoc) =>
            enrichClassroomTeacher({ id: classroomDoc.id, ...classroomDoc.data() }),
          )) as Class[];

          return { data: classrooms };
        } catch (error: any) {
          return {
            error: {
              status: "CUSTOM",
              message: error.message,
            },
          };
        }
      },
      keepUnusedDataFor: 60,
      async onCacheEntryAdded(
        etablissementId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe: (() => void) | undefined;

        try {
          await cacheDataLoaded;
          const userId = etablissementId || auth.currentUser?.uid;
          if (!userId) return;

          const classroomsQuery = query(
            collection(db, "classrooms"),
            where("createdBy", "==", userId),
            orderBy("createdAt", "desc"),
          );
          unsubscribe = onSnapshot(classroomsQuery, async (snapshot) => {
            const classrooms = await Promise.all(snapshot.docs.map((classroomDoc) =>
              enrichClassroomTeacher({ id: classroomDoc.id, ...classroomDoc.data() }),
            )) as Class[];
            updateCachedData(() => classrooms);
          });
        } catch (error) {
          console.error("Erreur de souscription aux classes de l'établissement:", error);
        }

        await cacheEntryRemoved;
        unsubscribe?.();
      },
      providesTags: ["classetablissements"],
    }),

    getClassById: builder.query({
      queryFn: async (classId) => {
        try {
          const classRef = doc(db, "classrooms", classId);
          const classSnap = await getDoc(classRef);

          if (!classSnap.exists()) {
            return { error: { status: 404, message: "Classe introuvable !" } };
          }

          const classData = classSnap.data();
          const studentIds: string[] = classData?.students ?? [];

          const studentsData = await Promise.all(
            studentIds.map(async (uid) => {
              const userSnap = await getDoc(doc(db, "users", uid));
              return { uid: userSnap.id, ...userSnap.data() } as User;
            })
          );

          return {
            data: {
              id: classSnap.id,
              ...classData,
              studentsData,
            } as unknown as Class,
          };
        } catch (error: any) {
          return { error: { status: 500, message: error.message } };
        }
      },
      providesTags: ["class"],
    }),
    // Supprimer
    deleteClass: builder.mutation({
      queryFn: async (id) => {
        try {
          const docRef = doc(db, "classrooms", id);
          await deleteDoc(docRef);
          return {
            data: { id, message: "Classe supprimée !" },
          };
        } catch (error: any) {
          return { error: { status: "DELETE_ERROR", message: error.message } };
        }
      },
      invalidatesTags: ["class", "classprofs", "classetablissements"],
    }),
    importClasse: builder.mutation({
      async queryFn(rows: Record<string, unknown>[]) {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }

          const response = await importClassrooms(rows);

          return { data: response };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["class", "classprofs", "classetablissements"],
    }),
  }),
});

export const {
  useAddOrEditClassMutation,
  useDeleteClassMutation,
  useLazyGetClassByIdQuery,
  useGetClassQuery,
  useGetClassroomsByProfQuery,
  useGetClassroomsByEtablissementQuery,
  useImportClasseMutation,
} = ClassApi;

export function useClassFromLocation(): [
  Class,
  boolean,
  string,
  (id: string) => any
] {
  const itemState = useLocationState<Class | undefined>(undefined);
  const [item, setItem] = useState(itemState);
  const { id } = useParams<{ id: string }>();
  const [findByClass, { data, isError, isLoading }] = useLazyGetClassByIdQuery();
  const navigate = useNavigate();

  useEffect(() => {
    // console.log("is id", id)
    if (id && id !== undefined) {
      // console.log("id", id)
      findByClass(id as string);
    }
  }, [id]);
  useEffect(() => {
    if (isError && !itemState) {
      navigate(-1);
    }
  }, [isError]);
  useEffect(() => {
    if (data) {
      setItem(data as Class);
    }
  }, [data]);

  return [item as Class & { bio: string }, isLoading, id as string, findByClass];
}
